import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import './Styles.css';
import { apiFetch, normalizeMenus, normalizeVillas } from './api';

const ALLOWED_CATEGORIES = [
    "breakfast_packages",
    "breakfast",
    "drinks",
    "fruits",
    "extras"
];

function ExistingOrdersPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const baseUrl = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000/orders/api';
    const backgroundImageUrl = window._env_?.REACT_APP_VILLA_BACKGROUND_IMAGE || process.env.REACT_APP_BACKGROUND_IMAGE;

    const [navbarOpen, setNavbarOpen] = useState(false);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [villas, setVillas] = useState([]);
    const [markedRows, setMarkedRows] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [addRows, setAddRows] = useState({});
    const isAddButtonVisible = (groupIdx) => {
        const rows = addRows[String(groupIdx)];
        return !rows || rows.length === 0 || rows.every(row => !row.isEditing);
    };
    const hasCompletedAddRowsForGroup = (groupIdx) =>
        Array.isArray(addRows[String(groupIdx)]) && addRows[String(groupIdx)].some(row => row.isCompleted);

    const hasMarkedRowsForGroup = (groupIdx) =>
        markedRows.some(row => row.groupIdx === groupIdx);

    const villaId = searchParams.get('villa');
    const lastName = searchParams.get('lastName');
    const checkinDate = searchParams.get('arrivalDate');
    const originalSearch = searchParams.toString();
    const hasGuestIdentification = Boolean(villaId && lastName && checkinDate);

    const ensureGuestIdentification = () => {
        if (!hasGuestIdentification) {
            setError('Guest identification is missing. Please reopen the link from the reception.');
            return false;
        }
        return true;
    };

    useEffect(() => {
        if (hasGuestIdentification) return;

        setError('Guest identification is missing. Please reopen the link from the reception.');
        const redirectTimer = window.setTimeout(() => {
            navigate('/?reason=missing_guest_params', { replace: true });
        }, 1200);

        return () => window.clearTimeout(redirectTimer);
    }, [hasGuestIdentification, navigate]);

    useEffect(() => {
        const fetchMenuItems = async () => {
            try {
                const response = await apiFetch(`${baseUrl}/menus`);
                if (!response.ok) throw new Error('Error loading menu items.');
                const data = normalizeMenus(await response.json());
                setMenuItems(data.filter(item => ALLOWED_CATEGORIES.includes(item.category)));
            } catch (err) {
                setError(err.message);
            }
        };
        fetchMenuItems();
    }, [baseUrl]);

    const fetchOrders = async () => {
        setLoading(true);
        setError(null);
        const selectedVilla = villas.find(v => v.id === villaId);
        const villaName = selectedVilla ? selectedVilla.name : '';
        const queryParams = new URLSearchParams({
            villa_name: villaName,
            last_name: lastName,
            checkin_date: checkinDate
        });
        try {
            const response = await apiFetch(`${baseUrl}/orders_by_guest?${queryParams}`);
            if (!response.ok) throw new Error('Could not fetch orders. Please check your details.');
            const data = await response.json();
            setOrders(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const updateMenuPrices = async () => {
        try {
            const orderItems = orders.flatMap(order =>
                (order.details || []).map(detail => ({
                    id: detail.id,
                    quantity: detail.quantity
                }))
            );
            const addRowItems = Object.values(addRows).flatMap(groupRows =>
                (groupRows || []).filter(r => r.itemId && r.quantity).map(r => ({
                    id: r.itemId,
                    quantity: parseInt(r.quantity, 10)
                }))
            );
            const items = [...orderItems, ...addRowItems];

            const response = await apiFetch(`${baseUrl}/menus/update_prices`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items })
            });
            if (!response.ok) throw new Error('Error updating prices.');
            const updatedMenus = await response.json();

            setAddRows(prev => {
                const newAddRows = { ...prev };
                Object.keys(newAddRows).forEach(groupIdx => {
                    newAddRows[groupIdx] = newAddRows[groupIdx].map(row => {
                        if (row.itemId && row.quantity) {
                            const found = updatedMenus.find(m => String(m.id) === String(row.itemId));
                            if (found) {
                                return { ...row, price: found.price };
                            }
                        }
                        return row;
                    });
                });
                return newAddRows;
            });
            setOrders(prevOrders =>
                prevOrders.map(order => ({
                    ...order,
                    details: order.details.map(detail => {
                        const found = updatedMenus.find(m => String(m.id) === String(detail.id));
                        if (found) {
                            return { ...detail, price: found.price };
                        }
                        return detail;
                    })
                }))
            );
        } catch (err) {
            setError(err.message);
        }
    };

    const handleSubmitDelete = async (groupIdx) => {
        if (!ensureGuestIdentification()) return;

        const rowsToDelete = markedRows.filter(row => row.groupIdx === groupIdx);
        if (rowsToDelete.length === 0) return;

        const group = Object.values(groupedOrders)[groupIdx];

        const ordersToDelete = rowsToDelete.map(row => {
            const item = group.items[row.itemIdx];
            const order = orders.find(o =>
                o.appointment_date === group.date &&
                o.appointment_time === group.time &&
                o.details.some(d =>
                    d.id === item.id &&
                    d.name === item.name &&
                    d.price === item.price &&
                    d.quantity === item.quantity &&
                    (d.discount ?? 0) === (item.discount ?? 0)
                )
            );
            if (!order) return null;
            const orderDetailItem = order.details.find(d =>
                d.id === item.id &&
                d.name === item.name &&
                d.price === item.price &&
                d.quantity === item.quantity &&
                (d.discount ?? 0) === (item.discount ?? 0)
            );
            if (!orderDetailItem) return null;
            return {
                ...order,
                details: [orderDetailItem]
            };
        }).filter(Boolean);

        try {
            const queryParams = new URLSearchParams({
                villa_id: villaId,
                last_name: lastName,
                checkin_date: checkinDate,
            });
            const response = await apiFetch(`${baseUrl}/orders_by_guest?${queryParams.toString()}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(ordersToDelete),
            });
            if (!response.ok) throw new Error('Error deleting order(s).');
            setMarkedRows(markedRows.filter(row => row.groupIdx !== groupIdx));
            await fetchOrders();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleSubmitAddRows = async (groupIdx) => {
        if (!ensureGuestIdentification()) return;

        const group = Object.values(groupedOrders)[groupIdx];
        if (!group) return;
        const rows = addRows[String(groupIdx)] || [];
        // Use values from the first order in the table.
        const baseOrder = orders[0] || {};
        const guestId = baseOrder.guest_id;
        const villaId = baseOrder.villa_id;
        const arrivalDate = baseOrder.arrival_date;

        const newOrders = [];
        rows.forEach(row => {
            if (row.isCompleted) {
                const menuItem = menuItems.find(i => String(i.id) === String(row.itemId));
                if (!menuItem) return;
                newOrders.push({
                    id: null,
                    name: baseOrder.name || "Guest",
                    appointment_date: group.date,
                    appointment_time: group.time,
                    order_type: "online",
                    details: [{
                        id: menuItem.id,
                        name: menuItem.name,
                        quantity: parseInt(row.quantity, 10),
                        category: menuItem.category,
                        price: menuItem.price,
                        discount: 0.0
                    }],
                    notes: "",
                    guest_id: guestId,
                    villa_id: villaId,
                    arrival_date: arrivalDate,
                    confirmation_e_mail: null
                });
            }
        });
        if (newOrders.length === 0) return;
        try {
            const queryParams = new URLSearchParams({
                villa_id: villaId,
                last_name: lastName,
                checkin_date: checkinDate,
            });
            const response = await apiFetch(`${baseUrl}/orders_by_guest?${queryParams.toString()}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newOrders)
            });
            if (!response.ok) throw new Error("Error creating orders");
            setAddRows(prev => {
                const newAddRows = { ...prev };
                delete newAddRows[String(groupIdx)];
                return newAddRows;
            });
            fetchOrders();
        } catch (err) {
            setError(err.message);
        }
    };

    // --- IMPORTANT: Always use groupIdx as a string key. ---
    const handleAddRow = (groupIdx) => {
        setAddRows(prev => ({
            ...prev,
            [String(groupIdx)]: [
                ...(Array.isArray(prev[String(groupIdx)]) ? prev[String(groupIdx)] : []),
                { quantity: 1, itemId: '', isEditing: true, isCompleted: false, isNew: false, price: undefined }
            ]
        }));
    };

    const handleEditRow = (groupIdx, rowIdx) => {
        setAddRows(prev => {
            const rows = Array.isArray(prev[String(groupIdx)]) ? [...prev[String(groupIdx)]] : [];
            rows[rowIdx] = { ...rows[rowIdx], isEditing: true };
            return { ...prev, [String(groupIdx)]: rows };
        });
    };

    const handleAddRowChange = (groupIdx, rowIdx, field, value) => {
        setAddRows(prev => {
            const rows = Array.isArray(prev[String(groupIdx)]) ? [...prev[String(groupIdx)]] : [];
            rows[rowIdx] = { ...rows[rowIdx], [field]: value };

            if (
                rows[rowIdx].isEditing &&
                rows[rowIdx].quantity &&
                rows[rowIdx].itemId &&
                !rows[rowIdx].isCompleted
            ) {
                rows[rowIdx].isCompleted = true;
                rows[rowIdx].isNew = true;
                if (
                    rowIdx === rows.length - 1 &&
                    !(rows[rows.length - 1].quantity === 1 && rows[rows.length - 1].itemId === '' && !rows[rows.length - 1].isEditing)
                ) {
                    rows.push({ quantity: 1, itemId: '', isEditing: false, isCompleted: false, isNew: false, price: undefined });
                }
            }

            setTimeout(() => {
                updateMenuPrices();
            }, 0);

            return { ...prev, [String(groupIdx)]: rows };
        });
    };

    const handleRemoveRow = (groupIdx, rowIdx) => {
        setAddRows(prev => {
            const rows = Array.isArray(prev[String(groupIdx)]) ? [...prev[String(groupIdx)]] : [];
            rows.splice(rowIdx, 1);
            const newAddRows = { ...prev };
            if (rows.length === 0) {
                delete newAddRows[String(groupIdx)];
            } else {
                newAddRows[String(groupIdx)] = rows;
            }
            return newAddRows;
        });
    };

    useEffect(() => {
        const fetchVillas = async () => {
            try {
                const response = await apiFetch(`${baseUrl}/villas`);
                if (!response.ok) throw new Error('Error retrieving villas.');
                const data = await response.json();
                setVillas(normalizeVillas(data));
            } catch (err) {
                setError(err.message);
            }
        };
        fetchVillas();
    }, [baseUrl]);

    useEffect(() => {
        if (villas.length === 0) return;

        const selectedVilla = villas.find(v => v.id === villaId);
        const villaName = selectedVilla ? selectedVilla.name : '';

        if (!villaName || !lastName || !checkinDate) {
            setError("Guest identification is missing.");
            setLoading(false);
            return;
        }

        fetchOrders();
        // eslint-disable-next-line
    }, [baseUrl, villaId, lastName, checkinDate, villas]);

    const groupOrders = (ordersToGroup) => {
        if (!ordersToGroup || ordersToGroup.length === 0) return {};
        return ordersToGroup.reduce((acc, order) => {
            const key = `${order.appointment_date}_${order.appointment_time}`;
            if (!acc[key]) {
                acc[key] = {
                    date: order.appointment_date,
                    time: order.appointment_time,
                    items: [],
                };
            }
            if (order.details && order.details.length > 0) {
                order.details.forEach(item => {
                    acc[key].items.push(item);
                });
            }
            return acc;
        }, {});
    };

    const groupedOrders = groupOrders(orders);


    return (
        <div className="app-container" style={{ '--background-image-url': `url('${backgroundImageUrl}')` }}>
            <Navbar navbarOpen={navbarOpen} setNavbarOpen={setNavbarOpen} />
            <div className="main-content-container">
                <div className="button-row" style={{
                    padding: '2em',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center'}}>
                    <Link
                        to={`/?${originalSearch}`}
                        className="selection-link"
                        onClick={() => window.scrollTo(0, 0)}
                    >
                        BACK TO ORDERING
                    </Link>
                </div>
                <div className="checked-orders-container" style={{padding: '2em', minHeight: '60vh'}}>
                    <h2 className="text-center">Your existing orders</h2>
                    <div className="divider"></div>
                    {loading && <p className="text-center">Loading your orders...</p>}
                    {error && <p className="text-center menu-selection-error">{error}</p>}
                    {!loading && !error && (
                        <>
                            {orders.length === 0 ? (
                                <p className="text-center">No orders found for the selected criteria. Please check if you chose the correct villa or you entered the correct last name or checkin date.</p>
                            ) : (
                                Object.values(groupedOrders).map((group, index) => {
                                    const hasMarkedRows = markedRows.some(row => row.groupIdx === index);

                                    return (
                                        <div key={index} className="mb-2em">
                                            <p className="text-center font-bold">
                                                Your order for the date {new Date(group.date).toLocaleDateString('en-GB')} at {group.time}
                                            </p>
                                            <table className="orders-table">
                                                <thead>
                                                <tr>
                                                    <th>Quantity</th>
                                                    <th>Item</th>
                                                    <th>Price</th>
                                                    <th>Actions</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {group.items.map((item, itemIdx) => {
                                                    const isMarked = markedRows.some(row => row.groupIdx === index && row.itemIdx === itemIdx);

                                                    const handleMarkRow = () => {
                                                        if (isMarked) {
                                                            setMarkedRows(markedRows.filter(row => !(row.groupIdx === index && row.itemIdx === itemIdx)));
                                                        } else {
                                                            setMarkedRows([...markedRows, { groupIdx: index, itemIdx: itemIdx }]);
                                                        }
                                                    };

                                                    return (
                                                        <tr key={itemIdx}>
                                                            <td style={isMarked ? { backgroundColor: '#ffe5e5' } : {}}>{item.quantity}</td>
                                                            <td style={isMarked ? { backgroundColor: '#ffe5e5' } : {}}>{item.name}</td>
                                                            <td style={isMarked ? { backgroundColor: '#ffe5e5' } : {}}>
                                                                {item.price === 0 && item.discount > 0
                                                                    ? 'included'
                                                                    : item.price != null
                                                                        ? item.price.toLocaleString('en-GB') + ' IDR'
                                                                        : ''}
                                                            </td>
                                                            <td>
                                                                {isAddButtonVisible(index) && (
                                                                    <button
                                                                        className="small-selection-btn"
                                                                        onClick={handleMarkRow}
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}

                                                {(Array.isArray(addRows[String(index)]) ? addRows[String(index)] : []).map((row, rowIdx) => (
                                                    <tr
                                                        key={`addrow-${rowIdx}`}
                                                        style={
                                                            row.isCompleted
                                                                ? row.isNew
                                                                    ? { backgroundColor: '#fffbe6' }
                                                                    : { backgroundColor: '#e6ffe6' }
                                                                : {}
                                                        }
                                                    >
                                                        {row.isEditing ? (
                                                            row.isCompleted ? (
                                                                <>
                                                                    <td>{row.quantity}</td>
                                                                    <td>{menuItems.find(i => String(i.id) === String(row.itemId))?.name || ''}</td>
                                                                    <td>
                                                                        {(row.price !== undefined
                                                                            ? row.price
                                                                            : menuItems.find(i => String(i.id) === String(row.itemId))?.price || 0
                                                                        ).toLocaleString('en-GB') + ' IDR'}
                                                                    </td>
                                                                    <td>
                                                                        <button
                                                                            className="small-selection-btn"
                                                                            onClick={() => handleRemoveRow(index, rowIdx)}
                                                                        >
                                                                            Remove
                                                                        </button>
                                                                    </td>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <td>
                                                                        <select
                                                                            value={row.quantity}
                                                                            onChange={e => handleAddRowChange(index, rowIdx, 'quantity', e.target.value)}
                                                                        >
                                                                            {[...Array(10)].map((_, i) => (
                                                                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                                                                            ))}
                                                                        </select>
                                                                    </td>
                                                                    <td>
                                                                        <select
                                                                            value={row.itemId}
                                                                            onChange={e => handleAddRowChange(index, rowIdx, 'itemId', e.target.value)}
                                                                        >
                                                                            <option value="">Please choose...</option>
                                                                            {menuItems.map(item => (
                                                                                <option key={item.id} value={item.id}>
                                                                                    {item.name} ({item.category})
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </td>
                                                                    <td>
                                                                        {row.itemId
                                                                            ? (row.price !== undefined
                                                                                ? row.price
                                                                                : menuItems.find(i => String(i.id) === String(row.itemId))?.price || 0
                                                                            ).toLocaleString('en-GB') + ' IDR'
                                                                            : ''}
                                                                    </td>
                                                                    <td>
                                                                        {(row.quantity && row.itemId) ? (
                                                                            <button
                                                                                className="small-selection-btn"
                                                                                onClick={() => handleRemoveRow(index, rowIdx)}
                                                                            >
                                                                                Remove
                                                                            </button>
                                                                        ) : null}
                                                                    </td>
                                                                </>
                                                            )
                                                        ) : (
                                                            <>
                                                                <td colSpan={3}></td>
                                                                <td>
                                                                    <button
                                                                        className="small-selection-btn"
                                                                        onClick={() => handleEditRow(index, rowIdx)}
                                                                    >
                                                                        Add
                                                                    </button>
                                                                </td>
                                                            </>
                                                        )}
                                                    </tr>
                                                ))}
                                                {(!addRows[String(index)] || addRows[String(index)].length === 0) && (
                                                    <tr>
                                                        <td colSpan={3}></td>
                                                        <td>
                                                            {!hasMarkedRows ? (
                                                                <button
                                                                    className="small-selection-btn"
                                                                    onClick={() => handleAddRow(index)}
                                                                >
                                                                    Add
                                                                </button>
                                                            ) : (
                                                                <span
                                                                    className="small-selection-btn"
                                                                    style={{ visibility: 'hidden', display: 'inline-block' }}
                                                                >
                                                                    Add
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                                </tbody>
                                                <tfoot>
                                                <tr>
                                                    <td colSpan="2" className="total-price-label">Total Price:</td>
                                                    <td className="total-price-value">
                                                        {`${(group.items || []).reduce((sum, item) => {
                                                            const price = item.price != null ? item.price : 0;
                                                            const quantity = item.quantity != null ? item.quantity : 0;
                                                            return sum + (price * quantity);
                                                        }, 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} IDR`}
                                                    </td>
                                                    <td>
                                                        {hasMarkedRowsForGroup(index) ? (
                                                            <button
                                                                className="small-selection-btn"
                                                                onClick={() => handleSubmitDelete(index)}
                                                                disabled={!hasMarkedRowsForGroup(index) || !hasGuestIdentification}
                                                            >
                                                                Submit Changes
                                                            </button>
                                                        ) : hasCompletedAddRowsForGroup(index) ? (
                                                            <button
                                                                className="small-selection-btn"
                                                                onClick={() => handleSubmitAddRows(index)}
                                                                disabled={!hasCompletedAddRowsForGroup(index) || !hasGuestIdentification}
                                                            >
                                                                Submit Changes
                                                            </button>
                                                        ) : (
                                                            <button className="small-selection-btn" disabled>
                                                                Submit Changes
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                                </tfoot>
                                            </table>
                                            <div className="divider"></div>
                                        </div>
                                    );
                                })
                            )}
                        </>
                    )}
                </div>
                <Footer />
            </div>
        </div>
    );
}

export default ExistingOrdersPage;

